import EmailInput from "components/EmailInput"
import HeroGraphic from "components/HeroGraphic"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ReactComponent as ArrowSVG } from "images/arrowRight.svg"
import { ReactComponent as SaltboxSVG } from "images/companies/Logo2.svg"
import { ReactComponent as DavinciSVG } from "images/companies/davinci.svg"
import { ReactComponent as NimblSVG } from "images/companies/nimbl.svg"
import { ReactComponent as PrepFortSVG } from "images/companies/prepFort.svg"
import { ReactComponent as ShippingTreeSVG } from "images/companies/shippingTree.svg"
import { ReactComponent as WSISVG } from "images/companies/wsi.svg"
import BoxSVG from "images/illustrations/OpenBox.svg"
import PalletSVG from "images/illustrations/Pallet.svg"
import ConstantMarquee from "library/ConstantMarquee"
import { loader } from "library/Loader"
import { ScreenContext } from "library/ScreenContext"
import { usePinType } from "library/Scroll"
import { DesktopOnly, TabletMobileOnly } from "library/breakpointUtils"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useAnimation from "library/useAnimation"
import { getResponsivePixels } from "library/viewportUtils"
import { useContext, useRef } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"

export default function Hero() {
	const pallet = useRef<HTMLImageElement>(null)
	const box = useRef<HTMLImageElement>(null)
	const wrapper = useRef<HTMLDivElement>(null)
	const topLineRef = useRef<HTMLDivElement>(null)
	const topLineSecondHalfRef = useRef<HTMLSpanElement>(null)
	const pinType = usePinType()
	const { mobile, tablet } = useContext(ScreenContext)

	useAnimation(
		() => {
			if (!mobile && !tablet) {
				const tl = gsap.timeline({
					scrollTrigger: {
						trigger: wrapper.current,
						start: "clamp(top bottom)",
						end: "bottom top",
						scrub: 0.5,
					},
				})

				const yEase = "power1.inOut"
				const xEase = "power1.out"

				tl.to(
					box.current,
					{
						yPercent: 190,
						scale: 0.5,
						ease: yEase,
					},
					0,
				)

				tl.to(
					box.current,
					{
						xPercent: -200,
						ease: xEase,
					},
					0,
				)

				tl.to(
					pallet.current,
					{
						yPercent: 210,
						scale: 0.6,
						ease: yEase,
					},
					0,
				)

				tl.to(
					pallet.current,
					{
						xPercent: 150,
						ease: xEase,
					},
					0,
				)

				tl.to([pallet.current, box.current], {
					opacity: 0,
					duration: 0.2,
				})
			}

			if (mobile) return
			gsap.set(".product-image", { x: 0, xPercent: -50 })
			const initialTimeline = gsap.timeline({
				delay: 0.1,
				paused: true,
				onComplete: () => {
					if (!mobile && !tablet) {
						ScrollTrigger.create({
							trigger: ".product-image",
							start: "center center",
							end: () => `+=${getResponsivePixels(125)}`,
							pin: true,
							pinType,
						})
						ScrollTrigger.refresh()
					}
				},
			})

			initialTimeline.to(
				topLineRef.current,
				{
					x: getResponsivePixels(-50),
					duration: 1.25,
					ease: "power1.inOut",
				},
				0,
			)

			initialTimeline.to(
				`${Arrow.toString()}`,
				{
					scale: 1,
					transformOrigin: "left",
					duration: 1,
					ease: "power1.inOut",
				},
				0.25,
			)

			initialTimeline.to(
				topLineSecondHalfRef.current,
				{
					opacity: 1,
					duration: 1.25,
					ease: "power1.inOut",
				},
				0,
			)

			initialTimeline.to(
				[
					`${Title.toString()}`,
					`${StyledEmailInput.toString()}`,
					`${MarqueeWrapper.toString()}`,
				],
				{
					opacity: 1,
					duration: 1,
					ease: "power1.inOut",
					yPercent: -100,
				},
				0.25,
			)

			if (!tablet) {
				initialTimeline.to(
					pallet.current,
					{
						x: getResponsivePixels(1114),
						duration: 1,
						ease: "power1.inOut",
					},
					0.25,
				)

				initialTimeline.to(
					box.current,
					{
						x: getResponsivePixels(-846),
						duration: 1,
						ease: "power1.inOut",
					},
					0.25,
				)
			}

			initialTimeline.to(
				".product-image",
				{
					y: tablet ? 0 : getResponsivePixels(-874),
					duration: 1,
					ease: "power1.inOut",
				},
				0.25,
			)

			if (tablet) {
				initialTimeline.to(
					".product-image",
					{
						opacity: 1,
						duration: 1,
						delay: 1,
						ease: "power1.inOut",
					},
					0.25,
				)
			}

			const onLoad = () => {
				initialTimeline.play()
			}

			loader.addEventListener("end", onLoad)
			return () => loader.removeEventListener("end", onLoad)
		},
		[pinType, mobile, tablet],
		{ recreateOnResize: true },
	)

	return (
		<>
			<Wrapper ref={wrapper}>
				{!mobile && (
					<TopLine ref={topLineRef}>
						Centralize orders, control spend
						<Arrow />
						<span style={{ opacity: 0 }} ref={topLineSecondHalfRef}>
							Unlock savings.
						</span>
					</TopLine>
				)}
				<Title>
					<Strong>control</Strong> indirect spend from end-to-end.
				</Title>
				{mobile && (
					<TopLine>Centralize orders, control spend. Unlock savings.</TopLine>
				)}
				<StyledEmailInput buttonText="Book a Demo" />
				<MarqueeWrapper>
					<Marquee>
						<MarqueeContent>
							<SaltboxSVG />
							<NimblSVG />
							<ShippingTreeSVG />
							<WSISVG />
							<DavinciSVG />
							<PrepFortSVG />
						</MarqueeContent>
					</Marquee>
				</MarqueeWrapper>
				<DesktopOnly>
					<Pallet ref={pallet} />
					<Box ref={box} />
				</DesktopOnly>
				<TabletMobileOnly>
					<Pallet data-speed="0.9" />
					<Box data-speed="0.9" />
				</TabletMobileOnly>
			</Wrapper>
			<HeroGraphic />
		</>
	)
}

const Wrapper = styled.div`
	${fresponsive(css`
		position: relative;
		display: grid;
		place-items: center;
		gap: 24px;
		padding: 55px 110px 15px;
	`)}

	${ftablet(css`
		gap: 36px;
		padding: 86px 0 0;
	`)}

  ${fmobile(css`
		padding: 114px 17px 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	`)}
`

const TopLine = styled.div`
	${fresponsive(css`
		${textStyles.h6};
		color: ${colors.grey3};
		display: flex;
		gap: 10px;
		align-items: center;
		position: relative;
		left: 50px;
	`)}

	${fmobile(css`
		display: relative;
		text-align: left;
		width: 220px;
		left: 0;
	`)}
`

const Arrow = styled(ArrowSVG)`
	${fresponsive(css`
		width: 19px;
		height: 14px;
		scale: 0;

		path {
			stroke: ${colors.blue2};
		}
	`)}

	${fmobile(css`
		display: none;
	`)}
`

const Title = styled.div`
	${fresponsive(css`
		${textStyles.h3}
		text-align: center;
		width: 943px;
		margin: -5px 0;
		opacity: 0;
		position: relative;
		transform: translateY(100%);
	`)}

	${ftablet(css`
		${textStyles.h2};
		width: 881px;
	`)}

	${fmobile(css`
		${textStyles.h4};
		width: 322px;
		text-align: left;
		opacity: 1;
		transform: unset;
	`)}
`

const StyledEmailInput = styled(EmailInput)`
	${fresponsive(css`
		opacity: 0;
		position: relative;
		transform: translateY(100%);
	`)}

	${ftablet(css`
		width: 589px;
	`)}

	${fmobile(css`
		width: 342px;
		opacity: 1;
		transform: unset;
	`)}
`

const Box = styled.img.attrs({ src: BoxSVG, alt: "" })`
	${fresponsive(css`
		position: absolute;
		z-index: -2;
		width: 314px;
		height: 371px;
		rotate: 15deg;
		right: -942px;
		top: 25px;
	`)}

	${ftablet(css`
		right: -90px;
		top: 30px;
		transition: all 1s ease-out;
	`)}

	${fmobile(css`
		width: 157px;
		height: 186px;
		left: -98px;
		top: 10px;
		right: unset;
		transition: all 1s ease-out;
	`)}
`

const Pallet = styled.img.attrs({ src: PalletSVG, alt: "" })`
	${fresponsive(css`
		position: absolute;
		z-index: -2;
		width: 557px;
		height: 385px;
		left: -1319px;
		top: 0;
	`)}

	${ftablet(css`
		left: -278px;
		top: -12px;
		transition: all 1s ease-out;
	`)}

	${fmobile(css`
		width: 278px;
		height: 193px;
		right: -91px;
		left: unset;
		top: -16px;
		transition: all 1s ease-out;
	`)}
`

const Strong = styled.strong`
	color: ${colors.blue2};
`

const MarqueeWrapper = styled.div`
	${fresponsive(css`
		width: 100%;
		display: grid;
		align-items: center;
		justify-content: center;
		opacity: 0;
		position: relative;
		transform: translateY(100%);
		z-index: -3;
	`)}

	${ftablet(css`
		margin: 53px 0;
	`)}

	${fmobile(css`
		opacity: 1;
		transform: unset;
	`)}
`

const Marquee = styled(ConstantMarquee)`
	${fresponsive(css`
		align-items: center;
		overflow: clip;
		width: 841px;
		height: 101px;
		position: relative;
		z-index: -3;

		&::before {
			content: "";
			z-index: 2;
			position: absolute;
			top: 0;
			left: -2px;
			width: 50px;
			height: 100%;
			background: linear-gradient(
				to left,
				transparent 0%,
				${colors.alpine} 100%
			);
		}

		&::after {
			content: "";
			z-index: 2;
			position: absolute;
			top: 0;
			right: -2px;
			width: 50px;
			height: 100%;
			background: linear-gradient(
				to right,
				transparent 0%,
				${colors.alpine} 100%
			);
		}
	`)}

	${fmobile(css`
		width: 340px;
	`)}
`

const MarqueeContent = styled.div`
	${fresponsive(css`
		display: flex;
		gap: 55px;
		margin-left: 55px;

		svg {
			width: 130px;
			height: 51px;
		}
	`)}

	${fmobile(css`
		gap: 40px;
		margin-left: 40px;

		svg {
			width: 94px;
			height: 40px;
		}
	`)}
`
